// GuiaHandball — manual de la estrategia de handball para el cliente,
// publicado en /aprende (dashboard y, vía Perfil → Aprende, en la app).
// Fuente de verdad del contenido: bot/handball/MANUAL_HANDBALL_CLIENTE.md
// (y su PDF en outputs/EdgeElite_ManualHandball.pdf). Los gráficos de
// ejemplo son partidos reales de producción (public/manual_hb/*.png).
// Sin stake, sin unidades, sin gestión de banca (decisión Matías).
// Español neutro con "tú"; OVER/UNDER en inglés; sin exponer la fórmula.
import React, { useState } from 'react';

const IMG = (n) => `${process.env.PUBLIC_URL || ''}/manual_hb/${n}.png`;

function Ejemplo({ img, titulo, pie, children }) {
  return (
    <figure className="gh-fig">
      <figcaption className="gh-fig-t">{titulo}</figcaption>
      <img src={IMG(img)} alt={titulo} loading="lazy" />
      <p className="gh-fig-p">{pie}</p>
      {children}
    </figure>
  );
}

export default function GuiaHandball() {
  const [abierta, setAbierta] = useState(true);  // abierta por defecto: es contenido nuevo

  return (
    <section className="apr-section">
      <h2 className="apr-section-h">🤾 Estrategia Handball</h2>

      <div className="gh-card">
        <button className="gh-head" onClick={() => setAbierta((o) => !o)} aria-expanded={abierta}>
          <span className="gh-ic">🤾</span>
          <span className="gh-t">
            <strong>Manual del apostador — Handball</strong>
            <span className="gh-sub">OVER por alerta, UNDER leyendo el gráfico, ejemplos reales, ligas y checklist</span>
          </span>
          <span className={`gh-arr${abierta ? ' up' : ''}`}>▾</span>
        </button>

        {abierta && (
          <div className="gh-body">
            <h3>1. Qué es Edge Elite Handball</h3>
            <p>
              El motor sigue en vivo los partidos de handball de todo el mundo. En cada momento
              calcula <strong>cuántos goles espera que se anoten en total</strong> y lo compara con
              la <strong>línea Over/Under que ofrecen las casas en ese instante</strong>. A esa
              distancia la llamamos <strong>edge</strong>, y es el número que manda en todo este
              manual. La ventaja aparece cuando el mercado tarda en ajustar su línea y el motor lo
              detecta antes.
            </p>
            <p>Hay <strong>dos formas de entrar</strong>:</p>
            <ul>
              <li><strong>OVER</strong> — te llega como alerta al canal. El motor ya validó todo.</li>
              <li><strong>UNDER en partido cerrado</strong> — lo lees tú en el gráfico, con la regla exacta del punto 5.</li>
            </ul>

            <h3>2. Cómo se lee la alerta OVER</h3>
            <pre className="gh-msg">{`🤾 LIVE — Handball · France Proligue
Dijon @ Pontault-Combault — min 34

🎯 OVER 58.5 · Edge +3.1
⏱ 2ª mitad temprana

🚨 Entrada: AHORA`}</pre>
            <ul>
              <li><strong>Liga</strong>: confirma que es handball y en qué competencia.</li>
              <li><strong>Equipos</strong>: siempre en formato <em>visitante @ local</em>.</li>
              <li><strong>min 34</strong>: el minuto exacto en que se disparó la alerta.</li>
              <li><strong>OVER 58.5</strong>: el lado y la línea. Apuestas a que el total superará 58.5 goles.</li>
              <li><strong>Edge +3.1</strong>: cuánto supera la proyección a esa línea, en goles.</li>
              <li><strong>Tramo</strong>: en qué parte de la segunda mitad ocurrió.</li>
              <li><strong>Entrada: AHORA</strong>: la alerta ya pasó todos los filtros.</li>
            </ul>

            <h3>3. Cuándo entra una alerta OVER</h3>
            <ul>
              <li><strong>Segunda mitad, entre el minuto 34 y el 55.</strong> Nunca antes ni después.</li>
              <li><strong>Edge de +3.0 goles o más.</strong></li>
              <li><strong>Línea en vivo válida y verificada.</strong></li>
              <li><strong>Partido de liga.</strong> Nunca copas, amistosos ni selecciones.</li>
            </ul>
            <p>
              <strong>La entrada es inmediata:</strong> el motor ya validó el minuto, el edge, la
              línea y el tipo de partido. La ventaja es que el mercado todavía no ajustó, y la
              línea se mueve en uno o dos minutos. Si tu casa ya la movió más de 1 gol respecto a
              la alerta, <strong>déjala pasar</strong>.
            </p>
            <table className="gh-tab">
              <thead><tr><th>Respaldo del OVER</th><th>n</th><th>WR</th><th>Piso (Wilson LB)</th></tr></thead>
              <tbody>
                <tr><td>Backtest, datos nunca vistos (2024-2026)</td><td>203</td><td>70.0%</td><td>63.3%</td></tr>
                <tr><td>Producción, primeras 34 alertas</td><td>34</td><td>61.8%</td><td>45.0%</td></tr>
              </tbody>
            </table>
            <p className="gh-nota">El punto de equilibrio a cuota 1.91 es 52.4%. El "piso" es el acierto mínimo con 95% de confianza.</p>

            <h3>4. Cómo leer el gráfico — lo más importante del manual</h3>
            <div className="gh-warn">
              <strong>El gráfico tiene dos ejes independientes</strong>
              <ul>
                <li><strong>La curva de color es el EDGE</strong>: verde cuando es positivo, azul cuando es negativo. Se lee contra el <strong>eje izquierdo</strong>.</li>
                <li><strong>La línea ámbar es la línea O/U de la casa</strong>, contra el <strong>eje derecho</strong>, en goles.</li>
                <li><strong>La franja sombreada</strong> es la ventana 34-55, la única zona donde una señal es válida.</li>
                <li><strong>Las punteadas</strong> son los umbrales: <strong>+3 para OVER</strong> (verde) y <strong>−2 para UNDER</strong> (azul).</li>
              </ul>
              <p>
                <strong>Las dos curvas viven en escalas distintas.</strong> Que la línea ámbar se vea
                "más arriba", "más abajo" o "cruce" a la curva del edge <strong>no significa nada</strong>.
                Lo único que se compara es la curva del edge contra sus umbrales punteados, dentro
                de la franja.
              </p>
            </div>

            <Ejemplo img="01_over_dos_alertas" titulo="Ejemplo 1 · OVER con dos alertas"
              pie="Dos alertas reales del canal: el edge cruza +3 en el minuto 34 (línea 58.5) y otra vez en el 43 (línea 55.5). Final: 61 goles, las dos ganaron. Fíjate que el edge ya había estado sobre +3 en la primera mitad y no hubo alerta: estaba fuera de la franja." />

            <Ejemplo img="02_under_cerrado" titulo="Ejemplo 2 · UNDER en partido cerrado"
              pie="Champions League, diferencia de 1 gol. En el minuto 44 el edge cae a −3.3 con la línea todavía en 64.5. Dos minutos después la casa bajó la línea a 59. Final: 59 goles. Este gráfico enseña qué es el edge: el retraso del mercado. Esa ventana de dos minutos es tu entrada." />

            <Ejemplo img="03_under_no_entrar" titulo="Ejemplo 3 · Toca UNDER, pero NO se entra"
              pie="Diferencia de 15 goles al tocar −2. Partido definido: el que ganaba administró, el que perdía aceleró. El edge se disparó a +4.8, la línea subió de 60 a 74.5 y terminó con 77 goles. Tocar el umbral no basta: la regla del UNDER exige partido cerrado." />

            <Ejemplo img="04_fuera_de_ventana" titulo="Ejemplo 4 · Toca UNDER en la primera mitad, y NO se entra"
              pie="El edge toca −2 en el minuto 5 con la línea en 51.5. Faltaban 55 minutos. Terminó con 64 goles. Antes del minuto 34 el edge es ruido: la franja no es un adorno, es la condición." />

            <div className="gh-warn">
              <strong>🚫 No deduzcas reglas propias del gráfico</strong>
              <p>
                Circulan reglas inventadas mirando las curvas ("cuando la ámbar cruza a la verde,
                va UNDER"). <strong>Esas reglas no existen.</strong> Nacen de comparar dos líneas en
                escalas distintas, como comparar la altura de un termómetro con la de un
                velocímetro porque aparecen en el mismo dibujo. Lo que sí existe es lo que está en
                este manual: la curva del edge contra sus umbrales, dentro de la franja, con la
                condición de diferencia de goles. Nada más.
              </p>
            </div>

            <h3>5. UNDER en partido cerrado — la regla exacta</h3>
            <p>Se estudió con los mismos criterios que el OVER. <strong>Todas</strong> las condiciones a la vez:</p>
            <ul>
              <li><strong>Segunda mitad, minuto 34 al 55.</strong> Funciona mejor <strong>del 43 al 55</strong>.</li>
              <li><strong>Edge de −2.0 o menos</strong> (la curva azul toca o cruza la punteada azul).</li>
              <li><strong>Diferencia de goles de 3 o menos</strong> en ese momento. <strong>Esta es la condición que separa la señal del ruido.</strong></li>
              <li><strong>Línea en vivo válida</strong>, y el marcador actual todavía por debajo de ella.</li>
              <li><strong>Partido de liga.</strong> Nunca selecciones.</li>
            </ul>
            <p>
              Cuando se cumple todo, entras <strong>UNDER a la línea vigente</strong>, de inmediato.
              <strong> No esperes un edge más profundo</strong> (−3 o −4): el −2 con diferencia corta ya
              es la señal, y esperar solo le da tiempo a la casa de bajar la línea.
            </p>
            <p>
              <strong>Por qué funciona:</strong> el OVER vive en partidos definidos; el UNDER vive en
              partidos cerrados. Con diferencia de 1 a 3 goles en la segunda mitad los dos equipos
              juegan a no equivocarse: ataques largos, posesiones que se estiran, más faltas
              tácticas, más tiempos muertos. El ritmo cae y la línea de la casa sigue anclada en el
              ritmo de la primera mitad. Con diferencia grande pasa lo contrario (ejemplo 3): el que
              pierde acelera y el total sube.
            </p>
            <table className="gh-tab">
              <thead><tr><th>Respaldo del UNDER cerrado</th><th>n</th><th>WR</th><th>Piso (LB)</th></tr></thead>
              <tbody>
                <tr><td>Backtest, datos nunca vistos, minuto 34-55</td><td>272</td><td>59.9%</td><td>54.0%</td></tr>
                <tr><td>Backtest, datos nunca vistos, <strong>minuto 43-55</strong></td><td>116</td><td><strong>65.5%</strong></td><td><strong>56.5%</strong></td></tr>
                <tr><td>Producción, edge ≤ −2, diferencia ≤ 3</td><td>21</td><td>71.4%</td><td>50.0%</td></tr>
                <tr><td>Producción, edge ≤ −2, cualquier diferencia</td><td>52</td><td>69.2%</td><td>55.7%</td></tr>
              </tbody>
            </table>
            <table className="gh-tab">
              <thead><tr><th>Qué NO es una señal UNDER</th><th>Qué pasa</th><th>Backtest</th></tr></thead>
              <tbody>
                <tr><td>Diferencia de 4 a 7 goles</td><td>El que pierde acelera, el total sube</td><td>47.9% — pierde plata</td></tr>
                <tr><td>Diferencia de 8 o más</td><td>Partido roto, ritmo impredecible</td><td>46-53% — no sirve</td></tr>
                <tr><td>Primera mitad</td><td>Demasiado partido por delante</td><td>No se valida</td></tr>
                <tr><td>Esperar a −3 o −4</td><td>La línea te alcanza mientras esperas</td><td>No mejora al −2</td></tr>
              </tbody>
            </table>

            <h3>6. Ligas — dónde rinde cada lado</h3>
            <table className="gh-tab">
              <thead><tr><th>OVER por liga (backtest 2024-2026)</th><th>n</th><th>WR</th><th>Piso</th></tr></thead>
              <tbody>
                <tr><td>Francia · Starligue</td><td>38</td><td>78.9%</td><td>63.7%</td></tr>
                <tr><td>Dinamarca · Handboldligaen</td><td>37</td><td>78.4%</td><td>62.8%</td></tr>
                <tr><td>Noruega · Eliteserien</td><td>59</td><td>74.6%</td><td>62.2%</td></tr>
                <tr><td>Alemania · Bundesliga</td><td>111</td><td>63.1%</td><td>53.8%</td></tr>
                <tr><td>España · ASOBAL</td><td>27</td><td>63.0%</td><td>44.2%</td></tr>
                <tr><td>Mundiales</td><td>45</td><td>64.4%</td><td>49.8%</td></tr>
              </tbody>
            </table>
            <p>
              Todas las ligas grandes rinden por encima del equilibrio. Las que el motor cubre pero
              <strong> no estaban en el backtest</strong> (segundas divisiones, Proligue, Hungría,
              Polonia, Portugal, Argentina) van con menos respaldo: en producción dieron
              <strong> 52%</strong> contra <strong>89%</strong> de las validadas, con pocos partidos
              todavía. <strong>Una alerta OVER de una liga grande vale más que una de una liga menor.</strong>
            </p>
            <table className="gh-tab">
              <thead><tr><th>UNDER cerrado por liga (backtest 2024-2026)</th><th>n</th><th>WR</th><th>Piso</th></tr></thead>
              <tbody>
                <tr><td>Francia · Starligue</td><td>54</td><td><strong>72.2%</strong></td><td>59.1%</td></tr>
                <tr><td>Alemania · Bundesliga</td><td>25</td><td>64.0%</td><td>44.5%</td></tr>
                <tr><td>Dinamarca · Handboldligaen</td><td>47</td><td>61.7%</td><td>47.4%</td></tr>
                <tr><td>España · ASOBAL</td><td>81</td><td>59.3%</td><td>48.4%</td></tr>
                <tr><td>Noruega · Eliteserien</td><td>13</td><td>46.2%</td><td>23.2%</td></tr>
                <tr><td>Europeo de selecciones</td><td>15</td><td><strong>26.7%</strong></td><td>10.9%</td></tr>
              </tbody>
            </table>
            <p>
              Dos avisos: <strong>el UNDER no funciona en torneos de selecciones</strong>, y en Noruega
              tampoco convence. En producción ha rendido bien en Alemania 2ª, Dinamarca, Francia y
              Champions League, y mal en Portugal y en copas.
            </p>

            <h3>7. Resumen de las dos reglas</h3>
            <table className="gh-tab">
              <thead><tr><th></th><th>OVER</th><th>UNDER cerrado</th></tr></thead>
              <tbody>
                <tr><td>Cómo te llega</td><td>Alerta al canal</td><td>Lo lees en el gráfico</td></tr>
                <tr><td>Ventana</td><td>Minuto 34-55</td><td>Minuto 34-55, mejor 43-55</td></tr>
                <tr><td>Edge</td><td>≥ +3.0 (punteada verde)</td><td>≤ −2.0 (punteada azul)</td></tr>
                <tr><td>Diferencia de goles</td><td>Sin condición</td><td><strong>3 o menos</strong></td></tr>
                <tr><td>Partido</td><td>Solo liga</td><td>Solo liga, nunca selecciones</td></tr>
                <tr><td>Entrada</td><td>Inmediata</td><td>Inmediata</td></tr>
              </tbody>
            </table>

            <h3>8. Por qué puedes recibir dos alertas del mismo partido</h3>
            <p>
              La segunda mitad se divide en dos tramos: <strong>2ª mitad temprana</strong> (34 al 42) y
              <strong> 2ª mitad tardía</strong> (43 al 55). El motor puede alertar una vez en cada tramo.
              No es un error ni un mensaje repetido: son dos momentos distintos, con marcadores y
              líneas distintas (ejemplo 1).
            </p>

            <h3>9. Qué NO hacer</h3>
            <ul>
              <li>No apuestes OVER fuera de las alertas.</li>
              <li>No apuestes UNDER sin las cuatro condiciones: ventana, edge ≤ −2, diferencia ≤ 3, línea válida.</li>
              <li>No inventes reglas mirando la línea ámbar contra la curva. Están en escalas distintas.</li>
              <li>No persigas una alerta vieja: varios minutos después la línea ya se movió.</li>
              <li>No apuestes sin línea disponible ni busques una entrada parecida en otro mercado.</li>
              <li>No hagas chasing. No dobles tras una racha. No mezcles la señal con corazonadas propias.</li>
            </ul>

            <h3>10. Checklist antes de confirmar</h3>
            <p><strong>Si es OVER (alerta del canal):</strong></p>
            <ul className="gh-check">
              <li>¿Llegó por el canal oficial de Alertas Handball?</li>
              <li>¿El partido está entre el minuto 34 y el 55?</li>
              <li>¿La línea de mi casa está a 1 gol o menos de la línea de la alerta?</li>
              <li>¿Es un partido de liga?</li>
              <li>¿Estoy entrando ahora y no persiguiendo una alerta de hace rato?</li>
            </ul>
            <p><strong>Si es UNDER (lectura del gráfico):</strong></p>
            <ul className="gh-check">
              <li>¿Estamos entre el minuto 34 y el 55 — idealmente del 43 en adelante?</li>
              <li>¿La curva del edge tocó o cruzó la punteada azul (−2)?</li>
              <li>¿La diferencia de goles es de 3 o menos en este momento?</li>
              <li>¿El marcador actual está por debajo de la línea?</li>
              <li>¿Es un partido de liga y no una copa ni selecciones?</li>
            </ul>
            <p><strong>Siempre:</strong></p>
            <ul className="gh-check">
              <li>¿Registré la entrada para poder revisarla después con calma?</li>
              <li>¿Tomaría esta misma señal después de tres pérdidas seguidas? Si la respuesta es no, el problema no es la señal — es la disciplina.</li>
            </ul>

            <h3>11. Expectativas honestas</h3>
            <p>
              Ninguna de las dos reglas gana siempre. Vas a ver rachas perdedoras: son parte normal
              del juego. La ventaja se mide en el acumulado de muchas entradas, no en una tarde. Lo
              que sí puedes exigir es que cada regla de este manual haya pasado por controles reales,
              con datos que el motor nunca había visto.
            </p>

            <p className="gh-legal">
              Esta guía es análisis estadístico deportivo, no asesoría financiera. Los resultados
              pasados no garantizan resultados futuros.
            </p>
          </div>
        )}
      </div>

      <style>{`
        .gh-card { background: var(--bg-elev); border: 0.5px solid var(--border);
          border-radius: 14px; overflow: hidden; }
        .gh-head { display: flex; align-items: center; gap: 12px; width: 100%;
          padding: 16px 18px; background: none; border: none; cursor: pointer; text-align: left; }
        .gh-ic { font-size: 22px; flex-shrink: 0; }
        .gh-t { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
        .gh-t strong { font-size: 15px; color: var(--ink); }
        .gh-sub { font-size: 12.5px; color: var(--muted); }
        .gh-arr { color: var(--muted); font-size: 14px; transition: transform .18s; }
        .gh-arr.up { transform: rotate(180deg); }
        .gh-body { padding: 4px 20px 22px; border-top: 0.5px solid var(--border); }
        .gh-body h3 { font-size: 13.5px; font-weight: 700; color: var(--ink);
          margin: 22px 0 8px; letter-spacing: -0.01em; }
        .gh-body p { font-size: 13.5px; line-height: 1.62; color: var(--ink-2, var(--ink));
          margin: 0 0 10px; }
        .gh-body ul { margin: 0 0 10px; padding-left: 20px; }
        .gh-body li { font-size: 13.5px; line-height: 1.62; color: var(--ink-2, var(--ink));
          margin-bottom: 6px; }
        .gh-check li::marker { content: '☐  '; }
        .gh-msg { background: var(--bg); border: 0.5px solid var(--border); border-radius: 10px;
          padding: 14px 16px; font-size: 12.5px; line-height: 1.55; color: var(--ink);
          white-space: pre-wrap; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          margin: 0 0 12px; overflow-x: auto; }
        .gh-warn { background: var(--bg); border: 0.5px solid var(--border);
          border-left: 3px solid var(--brand, #FF5C1A); border-radius: 10px;
          padding: 14px 16px; margin: 14px 0; }
        .gh-warn > strong { display: block; font-size: 13.5px; color: var(--ink);
          margin-bottom: 8px; }
        .gh-tab { width: 100%; border-collapse: collapse; font-size: 12.5px; margin: 6px 0 12px;
          display: block; overflow-x: auto; }
        .gh-tab th, .gh-tab td { text-align: left; padding: 7px 10px;
          border-bottom: 0.5px solid var(--border); color: var(--ink-2, var(--ink)); }
        .gh-tab th { color: var(--muted); font-weight: 600; font-size: 11.5px;
          text-transform: uppercase; letter-spacing: .03em; }
        .gh-nota { font-size: 12px !important; color: var(--muted) !important; margin-top: -4px !important; }
        .gh-fig { margin: 16px 0 20px; }
        .gh-fig img { width: 100%; height: auto; border-radius: 10px;
          border: 0.5px solid var(--border); display: block; }
        .gh-fig-t { font-size: 13.5px; font-weight: 700; color: var(--ink); margin: 0 0 8px; }
        .gh-fig-p { font-size: 12.5px !important; color: var(--muted) !important;
          margin: 8px 0 0 !important; line-height: 1.55 !important; }
        .gh-legal { font-size: 11.5px; color: var(--muted); font-style: italic;
          margin-top: 18px; padding-top: 12px; border-top: 0.5px solid var(--border); }
      `}</style>
    </section>
  );
}
